import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, X, Plus } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { inr, formatApiError } from "../lib/api";
import { Overline } from "../components/Reveal";
import { SUBCATEGORIES } from "../lib/catalog";

const TABS = ["overview", "analytics", "orders", "trade-ins", "products", "brands", "price-requests", "homepage"];
const CONDITIONS = ["new", "like new", "excellent", "very good", "good"];

const EMPTY_PRODUCT = { name: "", brand: "", brand_slug: "", gender: "women", category: "", price: "", mrp: "",
  stock: 10, condition: "new", is_preloved: false, sizes: "S,M,L", color: "", material: "", description: "",
  images: [], is_new_arrival: false, is_editors_pick: false, is_premium: false, rare_find: false };

function ProductEditor({ product, brands, onClose, onSaved }) {
  const [form, setForm] = useState(() => product ? {
    ...EMPTY_PRODUCT, ...product, sizes: (product.sizes || []).join(","),
    rare_find: (product.labels || []).includes("RARE FIND"),
  } : EMPTY_PRODUCT);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (files) => {
    [...files].forEach((file) => {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} is over 5MB`); return; }
      const reader = new FileReader();
      reader.onload = () => setForm((f) => ({ ...f, images: [...f.images, reader.result] }));
      reader.readAsDataURL(file);
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand_slug || !form.price) { toast.error("Name, brand and price are required"); return; }
    setBusy(true);
    const brandObj = brands.find((b) => b.slug === form.brand_slug);
    const payload = {
      ...form,
      brand: brandObj ? brandObj.name : form.brand || form.brand_slug,
      price: parseFloat(form.price), mrp: parseFloat(form.mrp || form.price),
      stock: parseInt(form.stock) || 0,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      labels: form.rare_find ? ["RARE FIND", ...(form.is_preloved ? ["PRE-LOVED", "VERIFIED"] : [])] : (form.is_preloved ? ["PRE-LOVED", "VERIFIED"] : []),
      condition: form.is_preloved ? form.condition : "new",
    };
    delete payload.product_id; delete payload.id; delete payload.sku; delete payload.created_at; delete payload.active; delete payload.rare_find; delete payload.verified; delete payload.condition_details;
    try {
      if (product) await api.patch(`/admin/products/${product.product_id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(product ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-transparent border border-border px-3 py-2.5 text-sm outline-none focus:border-wine transition-colors";

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="product-editor">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <form onSubmit={save} className="relative w-full max-w-xl bg-ivory h-full overflow-y-auto p-8">
        <div className="flex justify-between items-center">
          <h2 className="font-serif text-3xl">{product ? "Edit Product" : "New Product"}</h2>
          <button type="button" onClick={onClose} data-testid="editor-close" aria-label="Close"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <div className="mt-8 space-y-4">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Product name *" required className={inputCls} data-testid="pe-name" />
          <div className="grid grid-cols-2 gap-4">
            <select value={form.brand_slug} onChange={(e) => set("brand_slug", e.target.value)} required className={inputCls} data-testid="pe-brand">
              <option value="">Select brand *</option>
              {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
            </select>
            <select value={form.gender} onChange={(e) => { const g = e.target.value; setForm((f) => ({ ...f, gender: g, category: SUBCATEGORIES[g].includes(f.category) ? f.category : "" })); }} className={inputCls} data-testid="pe-gender">
              <option value="men">Men</option><option value="women">Women</option><option value="kids">Kids</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} required className={inputCls} data-testid="pe-category">
              <option value="">Category *</option>
              {SUBCATEGORIES[form.gender].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input value={form.sizes} onChange={(e) => set("sizes", e.target.value)} placeholder="Sizes (comma separated)" className={inputCls} data-testid="pe-sizes" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Price ₹ *" required className={inputCls} data-testid="pe-price" />
            <input type="number" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} placeholder="MRP ₹" className={inputCls} data-testid="pe-mrp" />
            <input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="Stock" className={inputCls} data-testid="pe-stock" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <input value={form.color} onChange={(e) => set("color", e.target.value)} placeholder="Color" className={inputCls} data-testid="pe-color" />
            <input value={form.material} onChange={(e) => set("material", e.target.value)} placeholder="Material" className={inputCls} data-testid="pe-material" />
          </div>
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} className={inputCls} data-testid="pe-description" />
          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_preloved} onChange={(e) => set("is_preloved", e.target.checked)} className="accent-[#4A0E17]" data-testid="pe-preloved" /> Pre-Loved</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_new_arrival} onChange={(e) => set("is_new_arrival", e.target.checked)} className="accent-[#4A0E17]" data-testid="pe-new-arrival" /> New Arrival</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_editors_pick} onChange={(e) => set("is_editors_pick", e.target.checked)} className="accent-[#4A0E17]" data-testid="pe-editors" /> Editor's Pick</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_premium} onChange={(e) => set("is_premium", e.target.checked)} className="accent-[#4A0E17]" data-testid="pe-premium" /> Premium</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.rare_find} onChange={(e) => set("rare_find", e.target.checked)} className="accent-[#D4AF37]" data-testid="pe-rare" /> Rare Find</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_home_pinned || false} onChange={(e) => set("is_home_pinned", e.target.checked)} className="accent-[#4A0E17]" data-testid="pe-pinned" /> Pin to Homepage</label>
          </div>
          {form.is_preloved && (
            <select value={form.condition} onChange={(e) => set("condition", e.target.value)} className={inputCls} data-testid="pe-condition">
              {CONDITIONS.filter((c) => c !== "new").map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </select>
          )}
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2">Images</p>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="pe-image-input" />
            <div className="grid grid-cols-4 gap-2">
              {form.images.map((src, i) => (
                <div key={i} className="relative aspect-[3/4] group">
                  <img src={src.startsWith("data:") || src.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("images", form.images.filter((_, j) => j !== i))} data-testid={`pe-image-remove-${i}`}
                    className="absolute top-1 right-1 bg-ink/70 text-ivory p-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove image"><X size={11} /></button>
                </div>
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="pe-image-add"
                className="aspect-[3/4] border border-dashed border-border hover:border-wine transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground">
                <Plus size={16} strokeWidth={1.5} /><span className="text-[9px] tracking-[0.15em] uppercase">Add Photo</span>
              </button>
            </div>
          </div>
          <button type="submit" disabled={busy} data-testid="pe-save"
            className="w-full bg-ink text-ivory py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors disabled:opacity-50">
            {busy ? "Saving…" : product ? "Save Changes" : "Create Product"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!data) return <p className="py-20 text-center font-serif text-2xl text-muted-foreground">Crunching numbers…</p>;

  const kpis = [
    ["Total Revenue (30d)", inr(data.total_revenue)], ["Avg Order Value", inr(data.avg_order_value)],
    ["New Sales", inr(data.new_sales)], ["Pre-Loved Sales", inr(data.preloved_sales)],
    ["Trade-Ins", data.tradein_total], ["Approval Rate", `${data.approval_rate}%`],
    ["Avg Cloth Cash Issued", inr(data.avg_cloth_cash_issued)],
  ];
  const axisStyle = { fontSize: 10, fontFamily: "Space Mono", fill: "#999" };
  const tipStyle = { background: "#111", border: "none", color: "#F9F8F6", fontSize: 12, fontFamily: "Space Mono" };
  const statusData = Object.entries(data.status_counts).map(([name, count]) => ({ name, count }));

  return (
    <div className="mt-10 space-y-8" data-testid="admin-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-border border border-border">
        {kpis.map(([l, v]) => (
          <div key={l} className="bg-ivory p-5">
            <p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground leading-relaxed">{l}</p>
            <p className="mt-2 font-mono text-xl">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-border p-6" data-testid="chart-sales">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">Sales · Last 30 Days</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.sales_series}>
              <CartesianGrid stroke="#EAE7E0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={axisStyle} axisLine={false} tickLine={false} interval={6} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`} />
              <Tooltip contentStyle={tipStyle} formatter={(v, name) => [name === "value" ? inr(v) : v, name === "value" ? "Revenue" : "Orders"]} labelFormatter={(d) => d} />
              <Area type="monotone" dataKey="value" stroke="#4A0E17" fill="#4A0E17" fillOpacity={0.12} strokeWidth={1.5} />
              <Area type="monotone" dataKey="orders" stroke="#D4AF37" fill="none" strokeWidth={1.5} strokeDasharray="4 3" />
            </AreaChart>
          </ResponsiveContainer>
          <p className="mt-3 text-[10px] text-muted-foreground font-mono">— Revenue &nbsp;·&nbsp; - - Orders</p>
        </div>

        <div className="border border-border p-6" data-testid="chart-cloth-cash">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">Cloth Cash · Earned vs Redeemed</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.cloth_cash_series}>
              <CartesianGrid stroke="#EAE7E0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={axisStyle} axisLine={false} tickLine={false} interval={6} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`} />
              <Tooltip contentStyle={tipStyle} formatter={(v, name) => [inr(v), name === "value" ? "Earned" : "Redeemed"]} />
              <Area type="monotone" dataKey="value" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.15} strokeWidth={1.5} />
              <Area type="monotone" dataKey="redeemed" stroke="#4A0E17" fill="#4A0E17" fillOpacity={0.08} strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
          <p className="mt-3 text-[10px] text-muted-foreground font-mono">— Earned &nbsp;·&nbsp; — Redeemed</p>
        </div>

        <div className="border border-border p-6" data-testid="chart-tradeins">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">Trade-In Submissions · Last 30 Days</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.tradein_series}>
              <CartesianGrid stroke="#EAE7E0" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={axisStyle} axisLine={false} tickLine={false} interval={6} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
              <Tooltip contentStyle={tipStyle} formatter={(v) => [v, "Submissions"]} />
              <Bar dataKey="value" fill="#111111" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border p-6" data-testid="chart-status">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">Trade-In Pipeline</p>
          {statusData.length === 0 ? <p className="text-sm text-muted-foreground">No Trade-Ins yet.</p> : (
            <div className="space-y-3">
              {statusData.map((s) => (
                <div key={s.name}>
                  <div className="flex justify-between text-xs mb-1"><span>{s.name}</span><span className="font-mono">{s.count}</span></div>
                  <div className="h-1.5 bg-sand"><div className="h-full bg-wine transition-all" style={{ width: `${(s.count / Math.max(...statusData.map((x) => x.count))) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-border p-6" data-testid="chart-top-brands">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">Top Trade-In Brands</p>
          {data.top_brands.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
            <div className="space-y-3">
              {data.top_brands.map((b) => (
                <div key={b.name}>
                  <div className="flex justify-between text-xs mb-1"><span>{b.name}</span><span className="font-mono">{b.count}</span></div>
                  <div className="h-1.5 bg-sand"><div className="h-full bg-gold" style={{ width: `${(b.count / data.top_brands[0].count) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border border-border p-6" data-testid="chart-top-categories">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-6">Top Trade-In Categories</p>
          {data.top_categories.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
            <div className="space-y-3">
              {data.top_categories.map((c) => (
                <div key={c.name}>
                  <div className="flex justify-between text-xs mb-1"><span>{c.name}</span><span className="font-mono">{c.count}</span></div>
                  <div className="h-1.5 bg-sand"><div className="h-full bg-ink" style={{ width: `${(c.count / data.top_categories[0].count) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [tradeIns, setTradeIns] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [orders, setOrders] = useState([]);
  const [priceRequests, setPriceRequests] = useState([]);
  const [settings, setSettings] = useState({ cloth_cash_percent: 15, referral_reward: 100, home_sections: {},
    redemption_rules: { max_percent: 25, min_order: 500, max_per_order: 1000, combine_promotions: true, allow_shipping: false, expiry_days: 365 },
    recovery_cloth_cash: 25, refund_to_cloth_cash: true, avaginja_url: "https://avaginja.com", offer_expiry_days: 7,
    whatsapp_configured: false });
  const [selected, setSelected] = useState(null);
  const [value, setValue] = useState("");
  const [offer, setOffer] = useState("");
  const [note, setNote] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [inspectNote, setInspectNote] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [statusF, setStatusF] = useState("");

  const statusCounts = tradeIns.reduce((m, t) => { m[t.status] = (m[t.status] || 0) + 1; return m; }, {});
  const filteredTIs = statusF ? tradeIns.filter((t) => t.status === statusF) : tradeIns;

  const bulkClassify = async (classification) => {
    if (classification === "reusable" && !(parseFloat(bulkValue) > 0)) {
      toast.error("Enter a cash amount (₹) for Reusable offers");
      return;
    }
    setBulkBusy(true);
    let ok = 0, failed = 0;
    for (const tid of selectedIds) {
      try {
        await api.post(`/admin/trade-ins/${tid}/action`, {
          action: "classify", classification,
          value: classification === "reusable" ? parseFloat(bulkValue) : undefined });
        ok++;
      } catch { failed++; }
    }
    setBulkBusy(false);
    setSelectedIds([]);
    setBulkValue("");
    toast.success(`Bulk ${classification}: ${ok} updated${failed ? `, ${failed} failed` : ""}`);
    load();
  };

  const toggleId = (tid) => setSelectedIds((prev) => prev.includes(tid) ? prev.filter((x) => x !== tid) : [...prev, tid]);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [newBrand, setNewBrand] = useState({ name: "", description: "", featured: false, trade_in_eligible: true });

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/trade-ins").then((r) => setTradeIns(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/price-requests").then((r) => setPriceRequests(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const act = async (tid, action, extra = {}) => {
    try {
      const { data } = await api.post(`/admin/trade-ins/${tid}/action`, {
        action, value: value ? parseFloat(value) : undefined,
        offer: offer ? parseFloat(offer) : undefined, note: note || undefined, ...extra });
      setTradeIns((prev) => prev.map((t) => (t.trade_in_id === tid ? data : t)));
      setSelected(data);
      setValue(""); setOffer(""); setNote("");
      toast.success(`Trade-In ${tid}: ${action.replace(/_/g, " ")}`);
      if (["approve_value", "set_offer", "adjust_offer"].includes(action)) toast.success("Offer email sent to customer");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convert = async (tid) => {
    try {
      const { data } = await api.post(`/admin/trade-ins/${tid}/convert-to-product`);
      toast.success(`Listed as draft product ${data.product.product_id} — set it Live in Products`);
      load();
    } catch (e) { toast.error(formatApiError(e), { duration: 6000 }); }
  };

  const saveRules = async () => {
    try {
      const { data } = await api.patch("/admin/settings", {
        redemption_rules: settings.redemption_rules, recovery_cloth_cash: settings.recovery_cloth_cash,
        refund_to_cloth_cash: settings.refund_to_cloth_cash, avaginja_url: settings.avaginja_url,
        offer_expiry_days: settings.offer_expiry_days });
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Redemption rules saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePercent = async () => {
    try {
      const { data } = await api.patch("/admin/settings", { cloth_cash_percent: settings.cloth_cash_percent });
      setSettings(data);
      toast.success(`Cloth Cash rule updated to ${data.cloth_cash_percent}%`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveReward = async () => {
    try {
      const { data } = await api.patch("/admin/settings", { referral_reward: settings.referral_reward });
      setSettings(data);
      toast.success(`Referral reward updated to ${inr(data.referral_reward)}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSection = async (key) => {
    try {
      const { data } = await api.patch("/admin/settings", { home_sections: { [key]: !settings.home_sections[key] } });
      setSettings((s) => ({ ...s, home_sections: data.home_sections }));
      toast.success(`Section ${data.home_sections[key] ? "shown" : "hidden"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePin = async (p) => {
    try {
      await api.patch(`/admin/products/${p.product_id}`, { is_home_pinned: !p.is_home_pinned });
      load();
      toast.success(p.is_home_pinned ? `${p.name} unpinned` : `${p.name} pinned to homepage`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const SECTION_LABELS = { featured: "Featured (Pinned Products)", new_arrivals: "New Arrivals", featured_brands: "Featured Brands",
    shop_by_category: "Shop by Category", preloved: "Pre-Loved Collection", rare_finds: "Rare Finds",
    trade_in_cta: "Trade-In / Cloth Cash", editors_picks: "Editor's Picks", promise: "The Jigel Promise", why: "Why Jigel" };

  const addBrand = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/brands", newBrand);
      setNewBrand({ name: "", description: "", featured: false, trade_in_eligible: true });
      toast.success("Brand added");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleBrand = async (slug, field, val) => {
    try { await api.patch(`/admin/brands/${slug}`, { [field]: val }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const inputCls = "bg-transparent border border-border px-3 py-2.5 text-sm outline-none focus:border-wine transition-colors";
  const btnCls = "px-4 py-2 border border-ink text-[10px] tracking-[0.2em] uppercase hover:bg-ink hover:text-ivory transition-colors";

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="admin-page">
      <Overline>Jigel Studio</Overline>
      <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter mt-3">Admin.</h1>

      <div className="mt-10 flex gap-1 border-b border-border overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`px-5 py-3.5 text-[11px] tracking-[0.2em] uppercase whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? "border-wine text-wine" : "border-transparent text-muted-foreground hover:text-ink"}`}>
            {t.replace("-", " ")}
          </button>
        ))}
      </div>

      {tab === "analytics" && <Analytics />}

      {tab === "overview" && stats && (
        <div className="mt-10" data-testid="admin-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border" data-testid="admin-stats">
            {[["Products", stats.total_products], ["Pre-Loved", stats.preloved_products], ["Trade-Ins", stats.trade_ins], ["Awaiting Review", stats.trade_ins_pending],
              ["Paid Orders", stats.orders], ["Customers", stats.customers], ["Cloth Cash Issued", inr(stats.cloth_cash_issued)]].map(([l, v]) => (
              <div key={l} className="bg-ivory p-7">
                <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{l}</p>
                <p className="mt-2 font-mono text-3xl">{v}</p>
              </div>
            ))}
            <div className="bg-gold/10 p-7 border-l-2 border-gold" data-testid="cloth-cash-setting">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Cloth Cash Rule</p>
              <div className="mt-2 flex items-center gap-2">
                <input type="number" min="1" max="100" value={settings.cloth_cash_percent}
                  onChange={(e) => setSettings({ ...settings, cloth_cash_percent: parseFloat(e.target.value) || 0 })}
                  className={`${inputCls} w-20 font-mono`} data-testid="cloth-cash-percent-input" />
                <span className="font-mono text-lg">%</span>
                <button onClick={savePercent} className={btnCls} data-testid="cloth-cash-percent-save">Save</button>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">Of approved Trade-In value</p>
            </div>
            <div className="bg-gold/10 p-7 border-l-2 border-gold" data-testid="referral-setting">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Referral Reward</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="font-mono text-lg">₹</span>
                <input type="number" min="0" value={settings.referral_reward}
                  onChange={(e) => setSettings({ ...settings, referral_reward: parseFloat(e.target.value) || 0 })}
                  className={`${inputCls} w-24 font-mono`} data-testid="referral-reward-input" />
                <button onClick={saveReward} className={btnCls} data-testid="referral-reward-save">Save</button>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">Cloth Cash per friend's first Trade-In</p>
            </div>
            <div className="bg-ivory p-7 border-l-2 border-border" data-testid="whatsapp-status">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">WhatsApp Alerts</p>
              {settings.whatsapp_configured ? (
                <p className="mt-2 text-sm text-gold font-medium">Connected — alerts send via WhatsApp</p>
              ) : (
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">Not configured. Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN and TWILIO_WHATSAPP_FROM to backend env to activate. Alerts are stored in-app meanwhile.</p>
              )}
            </div>
            <div className="bg-gold/10 p-7 border-l-2 border-gold col-span-2 md:col-span-4 lg:col-span-3" data-testid="redemption-rules-card">
              <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Cloth Cash Redemption Rules (JIGEL + AVAGINJA)</p>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["max_percent", "Max % of order"], ["min_order", "Min order ₹"], ["max_per_order", "Max per order ₹"], ["expiry_days", "Expiry (days)"]].map(([k, label]) => (                  <div key={k}>
                    <p className="text-[9px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{label}</p>
                    <input type="number" value={settings.redemption_rules[k]}
                      onChange={(e) => setSettings({ ...settings, redemption_rules: { ...settings.redemption_rules, [k]: parseFloat(e.target.value) || 0 } })}
                      className={`${inputCls} w-full font-mono`} data-testid={`rule-${k}`} />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
                <label className="flex items-center gap-2"><input type="checkbox" checked={settings.redemption_rules.combine_promotions} onChange={(e) => setSettings({ ...settings, redemption_rules: { ...settings.redemption_rules, combine_promotions: e.target.checked } })} className="accent-[#4A0E17]" data-testid="rule-combine" /> Combinable with promotions</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={settings.redemption_rules.allow_shipping} onChange={(e) => setSettings({ ...settings, redemption_rules: { ...settings.redemption_rules, allow_shipping: e.target.checked } })} className="accent-[#4A0E17]" data-testid="rule-shipping" /> Can pay shipping</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={settings.refund_to_cloth_cash} onChange={(e) => setSettings({ ...settings, refund_to_cloth_cash: e.target.checked })} className="accent-[#4A0E17]" data-testid="rule-refund" /> Refunds return Cloth Cash</label>
                <label className="flex items-center gap-2">Recovery Cloth Cash ₹<input type="number" value={settings.recovery_cloth_cash} onChange={(e) => setSettings({ ...settings, recovery_cloth_cash: parseFloat(e.target.value) || 0 })} className={`${inputCls} w-20 font-mono`} data-testid="rule-recovery" /></label>
                <label className="flex items-center gap-2">Offer expires after <input type="number" min="0" value={settings.offer_expiry_days} onChange={(e) => setSettings({ ...settings, offer_expiry_days: parseFloat(e.target.value) || 0 })} className={`${inputCls} w-20 font-mono`} data-testid="rule-offer-expiry" /> days</label>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <input value={settings.avaginja_url} onChange={(e) => setSettings({ ...settings, avaginja_url: e.target.value })} placeholder="AVAGINJA URL" className={`${inputCls} flex-1 font-mono text-xs`} data-testid="rule-avaginja-url" />
                <button onClick={saveRules} className={btnCls} data-testid="rules-save">Save Rules</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "price-requests" && (
        <div className="mt-10" data-testid="admin-price-requests">
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  {["Request", "Product", "Customer", "Phone", "Message", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {priceRequests.map((r) => (
                  <tr key={r.request_id} className="border-b border-border" data-testid={`price-request-${r.request_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{r.request_id}</td>
                    <td className="px-4 py-3">{r.product_name}</td>
                    <td className="px-4 py-3">{r.customer_name}<span className="block text-xs text-muted-foreground">{r.customer_email}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{r.phone}</td>
                    <td className="px-4 py-3 text-xs text-ink/60 max-w-48">{r.message || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 ${r.status === "responded" ? "bg-gold text-ink" : "bg-sand"}`}>{r.status}</span></td>
                    <td className="px-4 py-3">
                      {r.status === "new" && (
                        <button onClick={async () => { await api.post(`/admin/price-requests/${r.request_id}/respond`, { response: "Contacted" }); load(); toast.success("Marked responded"); }}
                          className={btnCls} data-testid={`respond-${r.request_id}`}>Mark Responded</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {priceRequests.length === 0 && <p className="p-10 text-center text-muted-foreground text-sm">No Ask-for-Price requests yet.</p>}
          </div>
        </div>
      )}

      {tab === "homepage" && (
        <div className="mt-10 grid lg:grid-cols-2 gap-10" data-testid="admin-homepage">
          <div className="border border-border h-fit">
            <p className="px-6 py-4 text-[10px] tracking-[0.25em] uppercase text-muted-foreground border-b border-border">Homepage Sections</p>
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between px-6 py-4 border-b border-border last:border-0" data-testid={`section-row-${key}`}>
                <span className="text-sm">{label}</span>
                <button onClick={() => toggleSection(key)} data-testid={`section-toggle-${key}`}
                  className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${settings.home_sections[key] !== false ? "border-gold text-gold" : "border-border text-muted-foreground"}`}>
                  {settings.home_sections[key] !== false ? "Visible" : "Hidden"}
                </button>
              </div>
            ))}
          </div>
          <div className="border border-border h-fit">
            <p className="px-6 py-4 text-[10px] tracking-[0.25em] uppercase text-muted-foreground border-b border-border">Pin Products to Homepage</p>
            <div className="max-h-[480px] overflow-y-auto">
              {products.map((p) => (
                <div key={p.product_id} className="flex items-center gap-3 px-6 py-3 border-b border-border last:border-0" data-testid={`pin-row-${p.product_id}`}>
                  <img src={p.images?.[0]?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${p.images[0]}` : p.images?.[0]} alt="" className="w-9 h-11 object-cover bg-sand" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{p.name}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground">{p.brand}</p>
                  </div>
                  <button onClick={() => togglePin(p)} data-testid={`pin-toggle-${p.product_id}`}
                    className={`text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-colors ${p.is_home_pinned ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                    {p.is_home_pinned ? "Pinned" : "Pin"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-10 overflow-x-auto border border-border" data-testid="admin-orders">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                {["Order ID", "Customer", "Items", "Subtotal", "Cloth Cash", "Total", "Status", "Date"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_id} className="border-b border-border" data-testid={`admin-order-${o.order_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                  <td className="px-4 py-3">{o.customer_name || "—"}<span className="block text-xs text-muted-foreground">{o.customer_email}</span></td>
                  <td className="px-4 py-3">{o.items?.length}</td>
                  <td className="px-4 py-3 font-mono">{inr(o.subtotal)}</td>
                  <td className="px-4 py-3 font-mono text-gold">{o.cloth_cash_used ? `−${inr(o.cloth_cash_used)}` : "—"}</td>
                  <td className="px-4 py-3 font-mono">{inr(o.total)}</td>
                  <td className="px-4 py-3"><span className="text-[10px] tracking-[0.15em] uppercase bg-sand px-2 py-1">{o.status.replace(/_/g, " ")}</span></td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {orders.length === 0 && <p className="p-10 text-center text-muted-foreground text-sm">No orders yet.</p>}
        </div>
      )}

      {tab === "trade-ins" && (
        <div className="mt-10" data-testid="admin-tradeins">
          <div className="mb-5 flex flex-wrap gap-2" data-testid="status-filter-bar">
            <button onClick={() => setStatusF("")} data-testid="filter-status-all"
              className={`px-4 py-2 text-[10px] tracking-[0.15em] uppercase border transition-colors ${!statusF ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
              All · {tradeIns.length}
            </button>
            {Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([s, c]) => (
              <button key={s} onClick={() => setStatusF(statusF === s ? "" : s)} data-testid={`filter-status-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={`px-4 py-2 text-[10px] tracking-[0.15em] uppercase border transition-colors ${statusF === s ? "bg-wine text-ivory border-wine" : "border-border hover:border-ink"}`}>
                {s === "Cloth Cash Offer Ready" ? "Offer Ready" : s} · {c}
              </button>
            ))}
          </div>
          {selectedIds.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 border border-wine/50 bg-wine/5 p-4" data-testid="bulk-bar">
              <span className="text-sm font-medium">{selectedIds.length} selected</span>
              <input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder="Cash ₹ (Reusable)" type="number"
                className={`${inputCls} w-40`} data-testid="bulk-value-input" />
              <button onClick={() => bulkClassify("reusable")} disabled={bulkBusy} className={`${btnCls} border-wine text-wine hover:bg-wine hover:text-ivory`} data-testid="bulk-reusable">Reusable → Cash</button>
              <button onClick={() => bulkClassify("good")} disabled={bulkBusy} className={btnCls} data-testid="bulk-good">Good → Cloth Cash</button>
              <button onClick={() => bulkClassify("recovery")} disabled={bulkBusy} className={btnCls} data-testid="bulk-recovery">Recovery</button>
              <button onClick={() => bulkClassify("rejected")} disabled={bulkBusy} className={`${btnCls} border-destructive text-destructive hover:bg-destructive hover:text-ivory`} data-testid="bulk-reject">Reject</button>
              <button onClick={() => setSelectedIds([])} className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground hover:text-ink" data-testid="bulk-clear">Clear</button>
            </div>
          )}
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-normal">
                    <input type="checkbox" data-testid="select-all"
                      checked={filteredTIs.length > 0 && filteredTIs.every((t) => selectedIds.includes(t.trade_in_id))}
                      onChange={(e) => setSelectedIds(e.target.checked ? filteredTIs.map((t) => t.trade_in_id) : [])}
                      className="accent-[#4A0E17] w-4 h-4" />
                  </th>
                  {["Trade-In ID", "Customer", "Brand", "Product", "Condition", "Year", "Used", "Value", "Status", "Cycle", ""].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filteredTIs.map((t) => (
                  <tr key={t.trade_in_id} className={`border-b border-border hover:bg-sand/40 cursor-pointer ${selectedIds.includes(t.trade_in_id) ? "bg-wine/5" : ""}`} onClick={() => { setSelected(t); setValue(t.suggested_value || ""); setOffer(""); setNote(""); }} data-testid={`admin-ti-row-${t.trade_in_id}`}>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.includes(t.trade_in_id)} onChange={() => toggleId(t.trade_in_id)}
                        className="accent-[#4A0E17] w-4 h-4" data-testid={`select-${t.trade_in_id}`} />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{t.trade_in_id}</td>
                    <td className="px-4 py-3">{t.customer_name}</td>
                    <td className="px-4 py-3">{t.brand_name}</td>
                    <td className="px-4 py-3">{t.item_type}</td>
                    <td className="px-4 py-3">{t.condition}</td>
                    <td className="px-4 py-3">{t.purchase_year || "—"}</td>
                    <td className="px-4 py-3">{t.years_used ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{t.approved_value ? inr(t.approved_value) : t.suggested_value ? `~${inr(t.suggested_value)}` : "—"}</td>
                    <td className="px-4 py-3"><span className="text-[10px] tracking-[0.15em] uppercase bg-sand px-2 py-1">{t.status}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{t.cycle_completed ? "YES" : "NO"}</td>
                    <td className="px-4 py-3 text-wine text-[10px] tracking-[0.2em] uppercase">Manage</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTIs.length === 0 && <p className="p-10 text-center text-muted-foreground text-sm">{statusF ? `No submissions with status "${statusF}".` : "No Trade-In submissions yet."}</p>}
          </div>

          {selected && (
            <div className="fixed inset-0 z-[70] flex justify-end" data-testid="admin-ti-detail">
              <div className="absolute inset-0 bg-ink/40" onClick={() => setSelected(null)} />
              <div className="relative w-full max-w-2xl bg-ivory h-full overflow-y-auto p-8">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm">{selected.trade_in_id}</p>
                    <h2 className="font-serif text-3xl mt-1">{selected.brand_name} · {selected.item_type}</h2>
                    <p className="text-xs text-muted-foreground mt-1">{selected.customer_name} · {selected.customer_email} · {selected.gender}</p>
                  </div>
                  <button onClick={() => setSelected(null)} data-testid="admin-ti-close" aria-label="Close"><X size={20} strokeWidth={1.5} /></button>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
                  {[["Size", selected.size], ["Color", selected.color], ["Material", selected.material], ["Purchase Year", selected.purchase_year],
                    ["Years Used", selected.years_used], ["Times Worn", selected.times_worn],
                    ["Purchase Price", selected.purchase_price && inr(selected.purchase_price)], ["Original MRP", selected.original_mrp && inr(selected.original_mrp)]].map(([k, v]) => (
                    <div key={k} className="border border-border p-3"><p className="text-[9px] tracking-[0.2em] uppercase text-muted-foreground">{k}</p><p className="mt-1">{v || "—"}</p></div>
                  ))}
                </div>

                <p className="mt-6 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Photographs</p>
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid="admin-ti-photos">
                  {(selected.photos || []).map((ph, i) => (
                    <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${ph}`} target="_blank" rel="noreferrer">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${ph}`} alt={`evidence ${i + 1}`} className="w-full aspect-[3/4] object-cover" />
                    </a>
                  ))}
                </div>

                {selected.condition_notes && <p className="mt-4 text-sm text-ink/60 border-l-2 border-gold pl-4">{selected.condition_notes}</p>}
                {selected.pickup_date && (
                  <p className="mt-4 text-sm border border-border px-4 py-3" data-testid="admin-pickup-info">
                    Pickup: <span className="font-mono">{selected.pickup_date}</span> · {selected.pickup_slot}
                  </p>
                )}

                <div className="mt-6 border border-wine/40 bg-wine/5 p-5" data-testid="classification-panel">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-wine">Classification</p>
                  <p className="mt-2 text-sm">
                    {selected.classification
                      ? <>Classified: <span className="capitalize font-medium text-wine">{selected.classification}</span>
                          {selected.payout_type && <span className="text-muted-foreground"> · {selected.payout_type === "cash" ? `Cash ${inr(selected.cash_amount || selected.offer || 0)}${selected.cash_paid ? " · Paid" : ""}` : "Cloth Cash"}</span>}</>
                      : "Not yet classified — choose an outcome below."}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 items-center">
                    <button onClick={() => act(selected.trade_in_id, "classify", { classification: "reusable" })} className={`${btnCls} border-wine text-wine hover:bg-wine hover:text-ivory`} data-testid="admin-classify-reusable">Reusable → Cash (enter ₹ above)</button>
                    <button onClick={() => act(selected.trade_in_id, "classify", { classification: "good" })} className={btnCls} data-testid="admin-classify-good">Good → Cloth Cash</button>
                    <button onClick={() => act(selected.trade_in_id, "classify", { classification: "recovery" })} className={btnCls} data-testid="admin-classify-recovery">Recovery</button>
                    <button onClick={() => act(selected.trade_in_id, "classify", { classification: "rejected" })} className={`${btnCls} border-destructive text-destructive hover:bg-destructive hover:text-ivory`} data-testid="admin-classify-rejected">Reject</button>
                    {selected.payout_type === "cash" && !selected.cash_paid && (
                      <button onClick={() => act(selected.trade_in_id, "mark_cash_paid")} className={`${btnCls} border-gold text-gold hover:bg-gold hover:text-ink`} data-testid="admin-mark-cash-paid">Mark Cash Paid</button>
                    )}
                  </div>
                </div>

                <div className="mt-6 border border-border p-5" data-testid="logistics-panel">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Pickup Logistics</p>
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <input value={agentName} onChange={(e) => setAgentName(e.target.value)} placeholder="Agent name" className={`${inputCls} w-36`} data-testid="agent-name-input" />
                    <input value={agentPhone} onChange={(e) => setAgentPhone(e.target.value)} placeholder="Agent phone" className={`${inputCls} w-36`} data-testid="agent-phone-input" />
                    <button onClick={() => { act(selected.trade_in_id, "assign_agent", { agent_name: agentName, agent_phone: agentPhone }); }} className={btnCls} data-testid="assign-agent-btn">Assign Agent</button>
                  </div>
                  {selected.pickup_agent?.name && <p className="mt-2 text-xs text-muted-foreground" data-testid="agent-display">Agent: {selected.pickup_agent.name} · {selected.pickup_agent.phone}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {["Scheduled", "Out for Pickup", "Collected", "Pickup Failed"].map((cs) => (
                      <button key={cs} onClick={() => act(selected.trade_in_id, "collection_status", { collection_status: cs })}
                        data-testid={`collection-${cs.toLowerCase().replace(/ /g, "-")}`}
                        className={`px-3 py-2 border text-[10px] tracking-[0.15em] uppercase transition-colors ${selected.collection_status === cs ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>{cs}</button>
                    ))}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input value={inspectNote} onChange={(e) => setInspectNote(e.target.value)} placeholder="Inspection note (physical condition vs photos…)" className={`${inputCls} flex-1`} data-testid="inspection-note-input" />
                    <button onClick={() => { act(selected.trade_in_id, "inspection_note", { note: inspectNote }); setInspectNote(""); }} className={btnCls} data-testid="inspection-note-btn">Add Note</button>
                  </div>
                  {selected.inspection_notes?.length > 0 && (
                    <div className="mt-3 space-y-1.5" data-testid="inspection-notes-list">
                      {selected.inspection_notes.map((n, i) => (
                        <p key={i} className="text-xs text-ink/60 border-l-2 border-gold pl-3">{n.note} <span className="text-muted-foreground">· {new Date(n.at).toLocaleDateString("en-IN")}</span></p>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mt-6 border border-gold/50 bg-gold/5 p-5" data-testid="valuation-panel">
                  <p className="text-[10px] tracking-[0.25em] uppercase text-gold">Trade-In Valuation</p>
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
                    <div><p className="text-[9px] tracking-[0.15em] uppercase text-muted-foreground">Suggested</p><p className="font-mono mt-1">{selected.suggested_value ? inr(selected.suggested_value) : "—"}</p></div>
                    <div><p className="text-[9px] tracking-[0.15em] uppercase text-muted-foreground">Approved Value</p><p className="font-mono mt-1">{selected.approved_value ? inr(selected.approved_value) : "—"}</p></div>
                    <div><p className="text-[9px] tracking-[0.15em] uppercase text-muted-foreground">Cloth Cash ({settings.cloth_cash_percent}%)</p><p className="font-mono mt-1 text-gold">{selected.offer ? inr(selected.offer) : "—"}</p></div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 items-center">
                    <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Trade-In value ₹" type="number" className={`${inputCls} w-40`} data-testid="admin-value-input" />
                    <button onClick={() => act(selected.trade_in_id, "set_value")} className={btnCls} data-testid="admin-set-value">Set Suggested</button>
                    <button onClick={() => act(selected.trade_in_id, "approve_value")} className={`${btnCls} border-gold text-gold hover:bg-gold hover:text-ink`} data-testid="admin-approve-value">Approve & Send Offer</button>
                  </div>
                  {["Final Inspection", "Received", "Cloth Cash Offer Ready"].includes(selected.status) && (
                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                      <input value={offer} onChange={(e) => setOffer(e.target.value)} placeholder="Adjusted Cloth Cash ₹" type="number" className={`${inputCls} w-44`} data-testid="admin-adjust-input" />
                      <button onClick={() => act(selected.trade_in_id, "adjust_offer")} className={btnCls} data-testid="admin-adjust-offer">Adjust Offer</button>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] tracking-[0.2em] uppercase bg-sand px-2.5 py-1.5" data-testid="admin-ti-status">{selected.status}</span>
                  <span className="text-[10px] tracking-[0.2em] uppercase border border-border px-2.5 py-1.5">Cycle Completed: {selected.cycle_completed ? "YES" : "NO"}</span>
                  {selected.authenticity_checked
                    ? <span className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 flex items-center gap-1 ${selected.verified ? "bg-gold text-ink" : "bg-destructive text-ivory"}`}><ShieldCheck size={11} /> {selected.verified ? "Authenticity Checked · Verified" : "Authenticity Checked · Not Verified"}</span>
                    : <span className="text-[10px] tracking-[0.2em] uppercase border border-border px-2.5 py-1.5 text-muted-foreground">Authenticity Not Checked</span>}
                </div>

                <div className="mt-8 border-t border-border pt-6 space-y-4">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to customer / internal note (optional)" className={`${inputCls} w-full`} data-testid="admin-note-input" />
                  <div className="flex flex-wrap gap-2" data-testid="admin-actions">
                    <button onClick={() => act(selected.trade_in_id, "approve")} className={btnCls} data-testid="admin-approve">Approve</button>
                    <button onClick={() => act(selected.trade_in_id, "reject")} className={btnCls} data-testid="admin-reject">Reject</button>
                    <button onClick={() => act(selected.trade_in_id, "request_photos")} className={btnCls} data-testid="admin-request-photos">Request Photos</button>
                    <button onClick={() => act(selected.trade_in_id, "request_info")} className={btnCls} data-testid="admin-request-info">Request Info</button>
                    <button onClick={() => act(selected.trade_in_id, "schedule_pickup")} className={btnCls} data-testid="admin-schedule-pickup">Schedule Pickup</button>
                    <button onClick={() => act(selected.trade_in_id, "mark_received")} className={btnCls} data-testid="admin-mark-received">Mark Received</button>
                    <button onClick={() => act(selected.trade_in_id, "final_inspection")} className={btnCls} data-testid="admin-final-inspection">Final Inspection</button>
                    <button onClick={() => act(selected.trade_in_id, "issue_cloth_cash")} className={`${btnCls} border-wine text-wine hover:bg-wine hover:text-ivory`} data-testid="admin-issue-cash">Issue Cloth Cash</button>
                    <button onClick={() => act(selected.trade_in_id, "complete")} className={btnCls} data-testid="admin-complete">Complete Trade-In</button>
                    <button onClick={() => act(selected.trade_in_id, "verify", { verified: true })} className={btnCls} data-testid="admin-verify">Mark Verified</button>
                    <button onClick={() => act(selected.trade_in_id, "verify", { verified: false })} className={btnCls} data-testid="admin-unverify">Mark Not Verified</button>
                    <button onClick={() => convert(selected.trade_in_id)} className={`${btnCls} border-wine text-wine hover:bg-wine hover:text-ivory`} data-testid="admin-convert">List as Product</button>
                  </div>
                  {selected.admin_notes?.length > 0 && (
                    <div className="text-xs text-muted-foreground space-y-1 pt-2">
                      {selected.admin_notes.map((n, i) => <p key={i}>· {n.note}</p>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "products" && (
        <div className="mt-10" data-testid="admin-products">
          <div className="flex justify-end mb-4">
            <button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="new-product-btn"
              className="bg-ink text-ivory px-6 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors flex items-center gap-2">
              <Plus size={14} strokeWidth={1.5} /> New Product
            </button>
          </div>
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  {["SKU", "Name", "Brand", "Dept", "Type", "Price", "MRP", "Stock", "Active"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.product_id} className="border-b border-border hover:bg-sand/40 cursor-pointer" onClick={() => { setEditing(p); setEditorOpen(true); }} data-testid={`admin-product-${p.product_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                    <td className="px-4 py-3">{p.name}</td>
                    <td className="px-4 py-3">{p.brand}</td>
                    <td className="px-4 py-3 capitalize">{p.gender}</td>
                    <td className="px-4 py-3">{p.is_preloved ? `Pre-Loved · ${p.condition}` : "New"}</td>
                    <td className="px-4 py-3 font-mono">{inr(p.price)}</td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{inr(p.mrp)}</td>
                    <td className="px-4 py-3 font-mono">{p.stock}</td>
                    <td className="px-4 py-3">
                      <button onClick={async (e) => { e.stopPropagation(); await api.patch(`/admin/products/${p.product_id}`, { active: !p.active }); load(); }}
                        data-testid={`admin-product-toggle-${p.product_id}`} className="text-[10px] tracking-[0.2em] uppercase border border-border px-2.5 py-1.5">
                        {p.active ? "Live" : "Hidden"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editorOpen && <ProductEditor product={editing} brands={brands} onClose={() => setEditorOpen(false)} onSaved={load} />}
        </div>
      )}

      {tab === "brands" && (
        <div className="mt-10 grid lg:grid-cols-[1fr_380px] gap-10" data-testid="admin-brands">
          <div className="overflow-x-auto border border-border h-fit">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                  {["Brand", "Pieces", "Featured", "Trade-In Eligible", "Status"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {brands.map((b) => (
                  <tr key={b.slug} className="border-b border-border" data-testid={`admin-brand-${b.slug}`}>
                    <td className="px-4 py-3 font-serif text-lg">{b.name}</td>
                    <td className="px-4 py-3 font-mono">{b.product_count}</td>
                    <td className="px-4 py-3"><button onClick={() => toggleBrand(b.slug, "featured", !b.featured)} data-testid={`admin-brand-featured-${b.slug}`} className="text-[10px] tracking-[0.15em] uppercase border border-border px-2 py-1">{b.featured ? "Yes" : "No"}</button></td>
                    <td className="px-4 py-3"><button onClick={() => toggleBrand(b.slug, "trade_in_eligible", !b.trade_in_eligible)} data-testid={`admin-brand-eligible-${b.slug}`} className={`text-[10px] tracking-[0.15em] uppercase border px-2 py-1 ${b.trade_in_eligible ? "border-gold text-gold" : "border-border"}`}>{b.trade_in_eligible ? "Eligible" : "Not Eligible"}</button></td>
                    <td className="px-4 py-3"><button onClick={() => toggleBrand(b.slug, "disabled", !b.disabled)} data-testid={`admin-brand-disable-${b.slug}`} className="text-[10px] tracking-[0.15em] uppercase border border-border px-2 py-1">{b.disabled ? "Disabled" : "Active"}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <form onSubmit={addBrand} className="border border-border p-6 h-fit space-y-4" data-testid="admin-add-brand">
            <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Add Brand</p>
            <input value={newBrand.name} onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })} placeholder="Brand name" required className={`${inputCls} w-full`} data-testid="admin-brand-name" />
            <input value={newBrand.description} onChange={(e) => setNewBrand({ ...newBrand, description: e.target.value })} placeholder="Description" className={`${inputCls} w-full`} data-testid="admin-brand-desc" />
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={newBrand.featured} onChange={(e) => setNewBrand({ ...newBrand, featured: e.target.checked })} data-testid="admin-brand-featured" /> Featured brand</label>
            <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={newBrand.trade_in_eligible} onChange={(e) => setNewBrand({ ...newBrand, trade_in_eligible: e.target.checked })} data-testid="admin-brand-eligible" /> Trade-In eligible</label>
            <button type="submit" className="w-full bg-ink text-ivory py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors" data-testid="admin-brand-submit">Add Brand</button>
          </form>
        </div>
      )}
    </div>
  );
}
